import { useStore } from '@nanostores/react';
import { memo, useCallback, useState } from 'react';
import { HistoryItem } from './HistoryItem';
import { binDates } from './date-binning';
import type { ChatHistoryItem } from '~/lib/persistence/chats';
import { chatStore } from '~/lib/stores/chat';
import { menuStore } from '~/lib/stores/menu';
import { classNames } from '~/utils/classNames';

export const ChatList = memo(() => {
  const [searchText, setSearchText] = useState<string>('');

  const list = useStore(chatStore.chatList);
  const isListLoading = useStore(chatStore.chatListLoading);

  const handleClearSearch = useCallback(() => {
    setSearchText('');
  }, []);

  return (
    <>
      <div className="p-4 select-none">
        <a
          href="/"
          className="flex gap-2 items-center bg-bolt-elements-sidebar-buttonBackgroundDefault text-bolt-elements-sidebar-buttonText hover:bg-bolt-elements-sidebar-buttonBackgroundHover rounded-md p-2 transition-theme"
        >
          <span className="inline-block i-bolt:chat scale-110" />
          Start new chat
        </a>
      </div>
      <div className="pl-4 pr-4 my-2">
        <div className="relative w-full">
          <input
            className={classNames(
              'w-full bg-white dark:bg-bolt-elements-background-depth-4 relative px-2 py-1.5 rounded-md focus:outline-none placeholder-bolt-elements-textTertiary border border-bolt-elements-borderColor',
              {
                'pr-6.5': !!searchText,
              },
            )}
            type="text"
            placeholder="Search"
            value={searchText}
            onChange={({ target }) => setSearchText(target.value)}
          />
          {searchText && (
            <button
              className="i-ph:x-bold absolute right-2 top-1/2 -translate-y-1/2 bg-none cursor-pointer text-bolt-elements-textTertiary hover:text-bolt-elements-textPrimary transition-theme"
              onClick={handleClearSearch}
            />
          )}
        </div>
      </div>
      <p className="text-bolt-elements-textPrimary font-medium pl-6 pr-5 my-2">Your Chats</p>
      <div className="flex-1 overflow-y-auto pl-4 pr-5 pb-5">
        {list.length === 0 && (
          <div className="pl-2 text-bolt-elements-textTertiary">
            {isListLoading ? (
              <>
                <span className="i-svg-spinners:90-ring-with-bg inline-block scale-110 mr-1.5" />
                <span>Loading previous conversations.</span>
              </>
            ) : (
              <span>No previous conversations</span>
            )}
          </div>
        )}

        {binDates(filterItems(searchText, list)).map(({ category, items }) => (
          <div key={category} className="mt-4 first:mt-0 flex flex-col gap-0.5">
            <p className="text-bolt-elements-textTertiary sticky top-0 z-1 bg-bolt-elements-background-depth-2 pl-2 pt-2 pb-1">
              {category}
            </p>
            {items.map((item) => (
              <HistoryItem
                key={item.projectId}
                item={item}
                onDelete={() => menuStore.openDialog({ type: 'delete', item })}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );
});

function filterItems(search: string, list: ChatHistoryItem[]) {
  return list.filter((item) => item.description?.toLowerCase().includes(search.toLowerCase()));
}
